import {takeUntil} from 'rxjs/operators';
import {BehaviorSubject, Subject} from 'rxjs';
import {Component, OnInit, OnDestroy} from '@angular/core';
import {SessionService} from '../../../shared/services/session.service';
import {LoggingService} from '../../../shared/services/logging.service';
import {HttpClient} from '@angular/common/http';

@Component({
  selector: 'quick-panel',
  templateUrl: './quick-panel.component.html',
  styleUrls: ['./quick-panel.component.scss'],
})
export class QuickPanelComponent implements OnInit, OnDestroy {
  date: Date;
  events: any[];
  notes: any[];
  settings: any;
  showStocks = true;
  notifyUpdates = true;
  showPartHealthHomeCharts = true;
  useRemoteFileSocketBackend = false;
  debugWebsockets = false;
  isPWA = window.matchMedia('(display-mode: standalone)').matches;

  torchToken: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  graphToken: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  isDevelopment = true;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   */
  constructor(private _sessionService: SessionService, private logger: LoggingService, private httpClient: HttpClient) {
    // Set the defaults
    this.date = new Date();
    this.settings = {
      notify: true,
      cloud: false,
      retro: true
    };

    this.useRemoteFileSocketBackend = _sessionService.useRemoteFileSocketBackend;
    this.debugWebsockets = _sessionService.debugWebsockets;

    // Set the private defaults
    this._unsubscribeAll = new Subject();

    this.isDevelopment = _sessionService.isDevelopment;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._sessionService.toolbarStockPrice
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((stocks) => {
        this.showStocks = stocks;
      });

    this._sessionService.partHomeHealthCharts
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((charts) => {
        this.showPartHealthHomeCharts = charts;
      });

    this._sessionService.notifyOnUpdates
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((notifyUpdates) => {
        this.notifyUpdates = notifyUpdates;
      });

    this._sessionService.tokens
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(tokens => {
      this.torchToken.next(tokens.torchToken);
      this.graphToken.next(tokens.graphToken);
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  updateShowStocks(event): void {
    this._sessionService.updateShowStockPrice(event.checked);
  }

  updateNotifyUpdates(event): void {
    this._sessionService.updateNotifyUpdates(event.checked);
  }

  updateShowPartHealthHomeCharts(event): void {
    this._sessionService.updateShowPartHomeHealthCharts(event.checked);
  }

  updateUseRemoteFileSocketBackend(event): void {
    this._sessionService.useRemoteFileSocketBackend = event.checked;
  }

  updateDebugWebsockets(event): void {
    this._sessionService.debugWebsockets = event.checked;
  }

  copyGraphTokenToClipboard() {
    this.graphToken.subscribe(token => {
      if (!token) {
        this.logger.error('No token available! This is an issue...');
      }

      navigator.clipboard.writeText(token).then(() => {
        this.logger.log('Copying token to clipboard was successful!');
      }, (err) => {
        this.logger.error('Could not copy token: ', err);
      });
    });
  }

  copyTorchTokenToClipboard() {
    this.torchToken.subscribe(token => {
      if (!token) {
        this.logger.error('No token available! This is an issue...');
      }

      navigator.clipboard.writeText(token).then(() => {
        this.logger.log('Copying token to clipboard was successful!');
      }, (err) => {
        this.logger.error('Could not copy token: ', err);
      });
    });
  }

  throwError() {
    throw new Error('Error from Quick Panel');
  }

  printLogMessages() {
    console.log(this.logger.logMessages);
  }

  throwNetError() {
    return this.httpClient.get('localhost:90909090909090').subscribe(() => {});
  }
}
